import {
  CancelOutlined,
  CheckCircleOutlined,
  CommentOutlined,
  DescriptionOutlined,
  ExpandMore,
  LocalOfferOutlined,
  ReceiptLongOutlined,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { useAppController } from 'customHooks/useAppController'
import { useBomItemThumbnailImageSrc } from 'features/BillOfMaterials/useBomItemThumbnailImageSrc'
import { ConfirmationDialog } from 'features/ConfirmationDialog/ConfirmationDialog'
import {
  IncomingQuotationsController,
  incomingQuotationRequestIds,
} from 'features/RequestForQuotations/Quotation/IncomingQuotationsController'
import { BomItemType } from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  MoneyDto,
  QuotationDto,
  IncomingQuotationDto,
  IncomingQuotationState,
  QuotationLineDto,
  RfqBoMItemReferenceDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import store from 'store/configureStore'
import { makeStyles } from 'tss-react/mui'
import { useShowException } from 'utils/useShowException'

const useStyles = makeStyles({ name: 'IncomingQuotationDetails' })((theme) => ({
  root: {
    marginTop: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: `calc(${theme.shape.borderRadius}px * 2)`,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: { flexDirection: 'column' },
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  totalsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2, 0),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
  },
  statCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.25),
    backgroundColor: theme.palette.background.default,
  },
  sectionHeader: { padding: theme.spacing(1.5, 2, 0) },
  sectionBody: { padding: theme.spacing(1, 2, 1.5) },
  sectionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: theme.spacing(1, 2),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
  },
  detailLabel: {
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  commentBox: {
    margin: theme.spacing(0, 2, 2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  tableWrap: {
    padding: theme.spacing(0, 2, 2),
    overflowX: 'auto',
  },
  activitiesCell: {
    display: 'flex',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
    minWidth: 120,
  },
  detailsToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1.25, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    userSelect: 'none',
    backgroundColor: theme.palette.background.default,
  },
  detailsToggleIcon: {
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  detailsToggleIconOpen: { transform: 'rotate(180deg)' },
  actionBar: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  completedNotice: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  centeredState: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}))

type IncomingQuotationDetailsProps = {
  incomingQuotationId: string
  bomItemReferences?: RfqBoMItemReferenceDto[]
  onActionComplete?: () => Promise<void> | void
  competingAwarded?: boolean
}

const formatMoney = (money: MoneyDto | null | undefined) => {
  if (money?.value === undefined) return '-'
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: money.currency || 'EUR',
    }).format(money.value)
  } catch {
    return `${money.value} ${money.currency || ''}`.trim()
  }
}

const formatDate = (value: Date | string | undefined) => {
  if (!value) return '-'
  const date = moment(value)
  return date.isValid() ? date.format('LL') : '-'
}

const formatVatRatio = (value: QuotationDto['vatRatio'] | undefined) => {
  if (value?.value === undefined) return '-'
  const code = value.unit || value.quantityType
  return `${value.value}${code ? ` ${code}` : ''}`
}

const stateColor = (state?: IncomingQuotationState) => {
  if (state === IncomingQuotationState.Accepted) return 'success' as const
  if (state === IncomingQuotationState.Rejected) return 'error' as const
  return 'default' as const
}

const completedStateMessage = (quotation: IncomingQuotationDto) => {
  if (quotation.state === IncomingQuotationState.Accepted) {
    return 'This supplier response has already been accepted.'
  }
  if (quotation.state === IncomingQuotationState.Rejected) {
    return quotation.rejectionReason
      ? `This quotation was rejected: ${quotation.rejectionReason}`
      : 'This supplier response has already been rejected.'
  }
  return 'No buyer action is available for this supplier response.'
}

const getBomItemReference = (
  line: QuotationLineDto,
  index: number,
  references?: RfqBoMItemReferenceDto[],
) =>
  references?.find(
    (reference) =>
      reference.sellerBoMItemId === line.id ||
      line.quotationBomItemLines?.some(
        (item) => item.boMItemId === reference.sellerBoMItemId,
      ),
  ) ?? references?.[index]

function QuotedLineItem({
  line,
  reference,
}: {
  line: QuotationLineDto
  reference?: RfqBoMItemReferenceDto
}) {
  const itemId = reference?.buyerBoMItemId
  const project = store.getState().project
  const bomItemPointer = {
    id: itemId,
    type:
      itemId && project?.assemblyHeaders?.[itemId]
        ? BomItemType.assemblyType
        : BomItemType.partType,
  } as BomItemPointer
  const { imageSrc } = useBomItemThumbnailImageSrc(bomItemPointer)
  const itemName = line.name || reference?.buyerOriginalName || '-'

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 220 }}>
      {imageSrc && (
        <Box
          component="img"
          src={imageSrc}
          alt={itemName}
          sx={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0 }}
        />
      )}
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {itemName}
      </Typography>
    </Box>
  )
}

export const IncomingQuotationDetails = ({
  incomingQuotationId,
  bomItemReferences,
  onActionComplete,
  competingAwarded = false,
}: IncomingQuotationDetailsProps) => {
  const { classes } = useStyles()
  const showException = useShowException('Request for Quotations')
  const { controller, loading } = useAppController(
    () => new IncomingQuotationsController(),
  )
  const [response, setResponse] = useState<IncomingQuotationDto | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [confirmAction, setConfirmAction] = useState<
    'accept' | 'reject' | null
  >(null)
  const [actionFeedback, setActionFeedback] = useState<string | null>(null)
  const [detailsExpanded, setDetailsExpanded] = useState(false)
  const [quotedLinesExpanded, setQuotedLinesExpanded] = useState(false)

  const fetchQuotation = useCallback(async () => {
    setIsFetching(true)
    try {
      setResponse(await controller.detail(incomingQuotationId))
    } catch (error) {
      showException(error)
      setResponse(null)
    } finally {
      setIsFetching(false)
    }
  }, [controller, incomingQuotationId, showException])

  useEffect(() => {
    void fetchQuotation()
  }, [fetchQuotation])

  const quotation = response?.quotation
  const lineCount = quotation?.lines?.length ?? 0
  const totalQuotedQuantity = useMemo(
    () =>
      (quotation?.lines ?? []).reduce(
        (total, line) => total + (line.quantity || 0),
        0,
      ),
    [quotation?.lines],
  )
  const isActionLoading = Boolean(
    loading[incomingQuotationRequestIds.accept(incomingQuotationId)] ||
    loading[incomingQuotationRequestIds.reject(incomingQuotationId)],
  )

  const overviewItems = [
    {
      label: 'Reference',
      value: quotation?.reference || quotation?.orderNumber,
    },
    { label: 'Order number', value: quotation?.orderNumber },
    { label: 'Supplier', value: quotation?.sellingPartyName },
    { label: 'Buyer', value: quotation?.buyingPartyName },
    { label: 'Created', value: formatDate(quotation?.dateCreated) },
    { label: 'Updated', value: formatDate(quotation?.dateChanged) },
  ].filter((item) => item.value && item.value !== '-')

  const commercialItems = [
    { label: 'Quotation date', value: formatDate(quotation?.quotationDate) },
    { label: 'Valid until', value: formatDate(quotation?.quotationExpireDate) },
    {
      label: 'Validity',
      value: quotation?.validityPeriodInDays
        ? `${quotation.validityPeriodInDays} days`
        : undefined,
    },
    { label: 'Currency', value: quotation?.currencyCode },
    { label: 'Payment terms', value: quotation?.paymentTermsCode },
    { label: 'Delivery terms', value: quotation?.deliveryTermsCode },
    { label: 'Language', value: quotation?.languageCode },
    { label: 'VAT ratio', value: formatVatRatio(quotation?.vatRatio) },
    {
      label: 'Exchange rate',
      value: quotation?.exchangeRate?.toString(),
    },
  ].filter((item) => item.value && item.value !== '-')

  const handleAction = async (action: 'accept' | 'reject') => {
    try {
      if (action === 'accept') {
        await controller.accept(incomingQuotationId)
        setActionFeedback('Quotation accepted.')
      } else {
        await controller.reject(incomingQuotationId)
        setActionFeedback('Quotation rejected.')
      }
      await fetchQuotation()
      await onActionComplete?.()
    } catch (error) {
      showException(error)
    } finally {
      setConfirmAction(null)
    }
  }

  if (isFetching) {
    return (
      <Paper className={classes.root} elevation={0}>
        <div className={classes.centeredState}>
          <CircularProgress size={28} />
        </div>
      </Paper>
    )
  }

  if (!response || !quotation) {
    return (
      <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
        No quotation details are available for this RFQ yet.
      </Alert>
    )
  }

  return (
    <Paper className={classes.root} elevation={0}>
      <div className={classes.header}>
        <div className={classes.headerText}>
          <span>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {quotation.sellingPartyName || 'Selling party name not found'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Review the received quotation details, then accept or reject it.
            </Typography>
          </span>
          <div className={classes.chipRow}>
            <Chip
              size="small"
              icon={<ReceiptLongOutlined />}
              label={response.state || 'Unknown state'}
              color={stateColor(response.state)}
              variant="outlined"
            />
            <Chip
              size="small"
              icon={<DescriptionOutlined />}
              label={`${lineCount} quoted line${lineCount === 1 ? '' : 's'}`}
              variant="outlined"
            />
            <Chip
              size="small"
              icon={<LocalOfferOutlined />}
              label={`${totalQuotedQuantity} total quantity`}
              variant="outlined"
            />
          </div>
        </div>
        <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
          <Typography variant="caption" color="textSecondary">
            Gross total
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {formatMoney(quotation.taxInclusiveAmount)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Net {formatMoney(quotation.taxExclusiveAmount)}
          </Typography>
        </Box>
      </div>

      {actionFeedback && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Alert severity="success" variant="outlined">
            {actionFeedback}
          </Alert>
        </Box>
      )}

      <div
        className={classes.detailsToggle}
        role="button"
        tabIndex={0}
        onClick={() => setDetailsExpanded((current) => !current)}
      >
        <ExpandMore
          fontSize="small"
          className={`${classes.detailsToggleIcon} ${
            detailsExpanded ? classes.detailsToggleIconOpen : ''
          }`}
        />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {detailsExpanded ? 'Hide supplier details' : 'Show supplier details'}
        </Typography>
      </div>
      <Collapse in={detailsExpanded}>
        <Divider />
        <div className={classes.totalsGrid}>
          {[
            ['Tax exclusive', quotation.taxExclusiveAmount],
            ['Tax inclusive', quotation.taxInclusiveAmount],
            ['Allowances', quotation.allowanceTotalAmount],
            ['Charges', quotation.chargeTotalAmount],
          ].map(([label, money]) => (
            <div className={classes.statCard} key={label as string}>
              <Typography variant="caption" color="textSecondary">
                {label as string}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {formatMoney(money as MoneyDto)}
              </Typography>
            </div>
          ))}
        </div>
        <Divider />
        {overviewItems.length > 0 && (
          <>
            <div className={classes.sectionHeader}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Supplier details
              </Typography>
            </div>
            <div className={classes.sectionBody}>
              <div className={classes.sectionGrid}>
                {overviewItems.map((item) => (
                  <div key={item.label}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      className={classes.detailLabel}
                    >
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.value}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
            <Divider />
          </>
        )}
        {commercialItems.length > 0 && (
          <>
            <div className={classes.sectionHeader}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Terms
              </Typography>
            </div>
            <div className={classes.sectionBody}>
              <div className={classes.sectionGrid}>
                {commercialItems.map((item) => (
                  <div key={item.label}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      className={classes.detailLabel}
                    >
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.value}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        {quotation.comment && (
          <>
            <Divider />
            <div className={classes.sectionHeader}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Supplier notes
              </Typography>
            </div>
            <div className={classes.commentBox}>
              <div className={classes.chipRow}>
                <CommentOutlined fontSize="small" color="action" />
                <Typography variant="subtitle2">Comment</Typography>
              </div>
              <Typography
                variant="body2"
                sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
              >
                {quotation.comment}
              </Typography>
            </div>
          </>
        )}
      </Collapse>

      <Divider />
      <div
        className={classes.detailsToggle}
        role="button"
        tabIndex={0}
        onClick={() => setQuotedLinesExpanded((current) => !current)}
      >
        <ExpandMore
          fontSize="small"
          className={`${classes.detailsToggleIcon} ${
            quotedLinesExpanded ? classes.detailsToggleIconOpen : ''
          }`}
        />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {quotedLinesExpanded ? 'Hide quoted lines' : 'Show quoted lines'}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {lineCount} line{lineCount === 1 ? '' : 's'}
        </Typography>
      </div>
      <Collapse in={quotedLinesExpanded}>
        {lineCount > 0 ? (
          <div className={classes.tableWrap}>
            <Table size="small" aria-label="Quoted lines">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Activities</TableCell>
                  <TableCell>Comment</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(quotation.lines ?? []).map((line, index) => {
                  const reference = getBomItemReference(
                    line,
                    index,
                    bomItemReferences,
                  )

                  return (
                    <TableRow key={line.id} hover>
                      <TableCell>
                        <QuotedLineItem line={line} reference={reference} />
                      </TableCell>
                      <TableCell>
                        <Box className={classes.activitiesCell}>
                          {(reference?.activities ?? []).length ? (
                            reference?.activities?.map(
                              (activity, activityIndex) => (
                                <WorkingStepIconSvg
                                  key={`${line.id}-${activity.workingStepType || 'unknown'}-${activityIndex}`}
                                  workingStepType={activity.workingStepType}
                                  attributes={{ width: '24px' }}
                                />
                              ),
                            )
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              -
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{line.comment || '-'}</TableCell>
                      <TableCell align="right">{line.quantity || 0}</TableCell>
                      <TableCell align="right">
                        {formatMoney(line.salesPrice)}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <Box sx={{ px: 2, pb: 2 }}>
            <Alert severity="info" variant="outlined">
              This response does not include any quoted lines.
            </Alert>
          </Box>
        )}
      </Collapse>

      {response.state === IncomingQuotationState.Received ? (
        <div className={classes.actionBar}>
          <Typography variant="caption" color="textSecondary">
            Accept this quotation to proceed with the supplier, or reject it to
            keep the RFQ open for other options.
          </Typography>
          <div className={classes.actionButtons}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelOutlined />}
              disabled={isActionLoading}
              onClick={() => setConfirmAction('reject')}
              size="small"
            >
              Reject quotation
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleOutlined />}
              disabled={isActionLoading || competingAwarded}
              onClick={() => setConfirmAction('accept')}
              size="small"
            >
              {competingAwarded
                ? 'Another supplier awarded'
                : 'Accept quotation'}
            </Button>
          </div>
        </div>
      ) : (
        <div className={classes.actionBar}>
          <div className={classes.completedNotice}>
            <CheckCircleOutlined color="success" fontSize="small" />
            <Typography variant="caption">
              {completedStateMessage(response)}
            </Typography>
          </div>
        </div>
      )}

      <ConfirmationDialog
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() =>
          confirmAction ? handleAction(confirmAction) : Promise.resolve()
        }
        title={
          confirmAction === 'accept' ? 'Accept quotation' : 'Reject quotation'
        }
        message={
          confirmAction === 'accept'
            ? `Accept the quotation from ${quotation.sellingPartyName || 'this supplier'}?`
            : `Reject the quotation from ${quotation.sellingPartyName || 'this supplier'}?`
        }
        confirmText={confirmAction === 'accept' ? 'Accept' : 'Reject'}
        cancelText="Go Back"
        disableAutoCloseOnConfirm
        busy={isActionLoading}
      />
    </Paper>
  )
}
